import { useEffect, useRef } from 'react';
import { useFilterStore } from '@/lib/FilterStore';

export const useFilterUrlSync = () => {
  const store = useFilterStore();
  const initRef = useRef(false);

  // Sync URL -> Store on mount
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const params = new URLSearchParams(window.location.search);
    
    // Query
    const q = params.get('q');
    if (q) store.setQuery(q);

    // Location
    const locLabel = params.get('loc');
    const lat = params.get('lat');
    const lng = params.get('lng');
    
    if (locLabel) {
      if (lat && lng) {
        store.setLocation({
          label: locLabel,
          lat: parseFloat(lat),
          lng: parseFloat(lng),
          source: 'manual'
        });
      } else {
        // If we only have label, set it. Geocoding might happen elsewhere if needed, 
        // or we assume it's just text search if no lat/lng provided.
        // But for exact filtering, lat/lng is best.
        store.setLocationLabel(locLabel);
      }
    }

    // Distance
    const dist = params.get('dist');
    if (dist) store.setDistance(parseInt(dist, 10));

    // Cuisines
    const cuisines = params.get('cuisines');
    if (cuisines) {
      store.setCuisines(cuisines.split(',').map(id => parseInt(id, 10)).filter(n => !isNaN(n)));
    }
    
    // Skills
    const skills = params.get('skills');
    if (skills) {
      store.setSkills(skills.split(',').map(id => parseInt(id, 10)).filter(n => !isNaN(n)));
    }

    // Toggles
    if (params.get('open') === '1') store.setOpenNow(true);
    if (params.get('delivery') === '1') store.setDeliveryOnly(true);
    
    // DeliveryMode (primary mode)
    const mode = params.get('mode');
    if (mode === 'pickup') store.setDeliveryMode('pickup');
    else if (mode === 'delivery') store.setDeliveryMode('delivery');
    
    // Sort
    const sort = params.get('sort');
    if (sort) store.setSortOption(sort);

  }, []);

  // Sync Store -> URL
  useEffect(() => {
    if (!initRef.current) return;

    const params = new URLSearchParams();
    
    if (store.query) params.set('q', store.query);
    
    if (store.location?.label) {
      params.set('loc', store.location.label);
      if (store.location.lat) params.set('lat', store.location.lat.toString());
      if (store.location.lng) params.set('lng', store.location.lng.toString());
    }

    // Only set if different from default (assuming 20 is default as per Store)
    if (store.distance && store.distance !== 20) params.set('dist', store.distance.toString());
    
    if (store.cuisineIds.length > 0) params.set('cuisines', store.cuisineIds.join(','));
    if (store.skills.length > 0) params.set('skills', store.skills.join(','));
    
    if (store.openNow) params.set('open', '1');
    if (store.deliveryOnly) params.set('delivery', '1');
    
    // DeliveryMode: nur speichern wenn nicht default ('delivery')
    if (store.deliveryMode && store.deliveryMode !== 'delivery') {
      params.set('mode', store.deliveryMode);
    }
    
    if (store.sortOption && store.sortOption !== 'Empfohlen') params.set('sort', store.sortOption);

    const newSearch = params.toString();
    const currentSearch = window.location.search.substring(1);

    if (newSearch !== currentSearch) {
       const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
       window.history.replaceState(null, '', newUrl);
    }

  }, [
    store.query, 
    store.location, 
    store.distance, 
    store.cuisineIds, 
    store.skills, 
    store.openNow, 
    store.deliveryOnly,
    store.deliveryMode,
    store.sortOption
  ]);
};
